/* purchase-block.js — behaviour for the "How to purchase" block
 * (snippets/purchase-block.liquid). Loaded by sections/main-product.liquid.
 *
 * Owns:
 *  1. STORE choice. The picker lists N showrooms (Settings > Showrooms, via
 *     snippets/showrooms.liquid); the choice persists as localStorage st.store
 *     and repaints the name / suburb in the button and every
 *     [data-store-inline] in the cards. With no choice made, the showroom
 *     whose "Postcodes served" covers the shopper's postcode wins (2300 ->
 *     Newcastle) — see readStore below.
 *  2. PLACE. One value site-wide: the header "Deliver to" chip
 *     (header-tabbed.js) owns localStorage st.postcode (+ st.locality via
 *     assets/locality.js) and exposes window.SydneyTaps.postcode
 *     {get,set(pc, locality?),clear,locality} + the st:postcode event.
 *     The card's "Suburb or postcode" field writes THROUGH that API when it
 *     exists (so the chip repaints "Lane Cove 2064") and falls back to the
 *     same keys when the page has no chip. Every change, from either end,
 *     re-quotes.
 *  3. QUOTE. GET /apps/sydneytaps/delivery-estimate?variant=&postcode=&qty=
 *  3b. OPTIONS. The same endpoint with options=1 returns the LIST of delivery
 *      options for THIS ITEM at THIS PAGE'S QUANTITY, priced by the checkout's
 *      own rate engine. Shown under the sentence, never instead of it: the
 *      sentence is the fallback when the app is unreachable, and its reserve
 *      box is a measured CLS device. See renderOptions().
 *
 *      THE WIDGET PRICES THE ITEM, THE CART PRICES THE CART (Luke, 2026-09-15,
 *      with two screenshots — a cart drawer saying "3 pallets × $100 = $300"
 *      beside this widget saying "4 pallets × $100 = $400" on the toilet he
 *      was looking at): "why are we showing 3 pallets in the cart, but four
 *      pallets in the product page calculator? … this shipping estimator
 *      should show the quantity currently input on the product page itself,
 *      the cart should show its own calculation for whatever's in the cart.
 *      Otherwise people will get confused at a larger price as it won't make
 *      sense to them."
 *
 *      He is right, and the earlier reading of "the threshold is a property of
 *      the cart" was wrong about WHERE that belongs. Adding the cart's lines to
 *      this request made the widget answer a question the shopper never asked
 *      — "what would delivery cost for everything I am carrying, plus this?" —
 *      under a heading that names one product, next to a quantity box that says
 *      1. The two pallet counts on screen were both correct and no shopper
 *      could have reconciled them. So: this request carries ONE line, this
 *      product at qty(), and nothing else. The cart-shaped `cart=1` mode stays
 *      for the drawer and the cart page, which are about the cart.
 *     (the supplier-sync app's App Proxy) ->
 *       { ok:true, carrier, service, price_cents, currency:"AUD",
 *         eta:{min_days,max_days}|null,
 *         rates:[{carrier,service,price_cents,eta,label}], source, cached_at }
 *       | { ok:false, reason }
 *     Rendered as "Delivery to Lane Cove 2064 — from $X.XX via <carrier>"
 *     plus "Arrives in N–M business days" ONLY when eta is present. eta
 *     null = no time claim at all. Luke 2026-09-09: "if no delivery time
 *     window estimate can be given, it's better not to mention it at all."
 *     When rates[] carries TWO entries (the backend found a cheapest and a
 *     genuinely different fastest) both are listed, labelled. With one
 *     carrier connected the backend sends one unlabelled row and this
 *     renders exactly as before — the same option is never shown twice.
 *     reason "no_dimensions" (no shipping weight/dims on the variant, so no
 *     carrier can be quoted) = "We can't calculate delivery for this item
 *     online." + a link to the contact page; never a price. Any OTHER
 *     ok:false, a non-2xx (the proxy is 404 until the app is installed on
 *     the store), a network failure or a malformed body = "Delivery quoted
 *     at checkout for Lane Cove 2064" + the freight note, and nothing about
 *     time.
 *     Re-quoted on st:postcode, variant:change (ui.js) and a quantity
 *     change; one in-flight request at a time (AbortController), cached per
 *     variant|postcode|qty for the page. The result line is aria-live and
 *     has two lines reserved (purchase-block.css) so no state shifts the
 *     buy box; the spinner appears only once a request outlives 200 ms.
 *  4. window.SydneyTaps.fulfilment = {get(){store,postcode,locality},
 *     setStore, setPostcode(pc, locality?)} and the st:fulfilment event —
 *     the one source for anything downstream (cart, checkout hints).
 *
 * ui.js's postcode zone table (SydneyTaps.postcodeEta) is no longer read
 * here — a range heuristic is exactly the kind of time claim the carrier
 * never made.
 *
 * FIRST PAINT is not ours either (2026-09-15). snippets/locality-boot.liquid,
 * rendered inline after the delivery card, fills [data-postcode] and names the
 * place in the estimate sentence before the browser paints; paintEstimate()
 * below compares before it writes, so it only ever changes what the quote
 * actually changed. */
(() => {
  'use strict';
  const root = document.querySelector('[data-purchase]');
  if (!root) return;

  const ST = (window.SydneyTaps = window.SydneyTaps || {});
  const STORE_KEY = 'st.store';
  const PC_KEY = 'st.postcode';
  /* The App Proxy BASE is ONE theme setting (Settings > Shipping promise >
   * "Shipping app proxy path"), carried on the options box. Shipping is moving
   * into its own custom app ("Sydney Taps Shipping", /apps/sydneytaps-shipping)
   * and the cutover must be that one field, not an edit in four files. */
  const PROXY_BASE = (function () {
    var box = document.querySelector('[data-delivery-options]');
    var v = box && box.dataset ? (box.dataset.proxy || '').trim() : '';
    return (v || '/apps/sydneytaps-shipping').replace(/\/+$/, '');
  }());
  const EST_URL = PROXY_BASE + '/delivery-estimate';
  const SPINNER_AFTER = 200;
  const PC = /^\d{4}$/;
  const lsGet = (k) => { try { return (localStorage.getItem(k) || '').trim(); } catch (e) { return ''; } };
  const lsSet = (k, v) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch (e) { /* private mode */ } };
  const LOC = () => ST.locality || null;

  /* ---------- store ---------- */
  const picker = root.querySelector('[data-store-picker]');
  const opts = picker ? [...picker.querySelectorAll('[data-store-opt]')] : [];
  /* "Postcodes served" (Settings > Showrooms) as "2280-2340, 2420": a list of
   * 4-digit postcodes and inclusive ranges. Compared as NUMBERS, so a range
   * written backwards still behaves and a stray space cannot change the answer.
   * A showroom with nothing in the field serves nowhere in particular — it is
   * reached as the fallback below, never as a match. */
  const servesPc = (el, pc) => {
    const n = parseInt(pc, 10);
    if (!n) return false;
    return (el.getAttribute('data-serves') || '').split(',').some((part) => {
      const r = part.trim();
      if (!r) return false;
      const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(r);
      if (!m) return parseInt(r, 10) === n;
      const a = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      return n >= Math.min(a, b) && n <= Math.max(a, b);
    });
  };
  /* WHICH SHOWROOM (2026-09-19, Ranya's Newcastle request). In order:
   *   1. the shopper's own choice — st.store, written only by a click on the
   *      picker. An explicit choice is never overridden by a postcode.
   *   2. the first showroom that serves their postcode: 2300 -> Newcastle.
   *   3. the first showroom in the list — the Sydney store.
   * Liquid rendered (3), so paintStore() runs on load and again on
   * st:postcode to move it. */
  const readStore = () => {
    const saved = lsGet(STORE_KEY);
    const hit = opts.find((o) => o.value === saved);
    if (hit) return hit.value;
    const pc = readPc();
    if (pc) {
      const near = opts.find((o) => servesPc(o, pc));
      if (near) return near.value;
    }
    const checked = opts.find((o) => o.checked) || opts[0];
    return checked ? checked.value : null;
  };
  const paintStore = () => {
    const id = readStore();
    const opt = opts.find((o) => o.value === id);
    if (!opt) return;
    opts.forEach((o) => { o.checked = o === opt; });
    const name = opt.getAttribute('data-name') || '';
    const short = opt.getAttribute('data-short') || name;
    const nameEl = picker.querySelector('[data-store-name]');
    const subEl = picker.querySelector('[data-store-sub]');
    if (nameEl && nameEl.textContent !== name) nameEl.textContent = name;
    if (subEl && subEl.textContent !== short) subEl.textContent = short;
    root.querySelectorAll('[data-store-inline]').forEach((el) => { if (el.textContent !== short) el.textContent = short; });
  };

  /* ---------- place (postcode + locality) ---------- */
  const readPc = () => (ST.postcode ? ST.postcode.get() : lsGet(PC_KEY));
  const readLoc = () => {
    const L = LOC();
    const loc = L ? L.get() : null;
    return loc && loc.postcode === readPc() ? loc : null;
  };
  const writePc = (pc, loc) => {
    pc = String(pc || '').replace(/\D/g, '').slice(0, 4);
    if (pc.length !== 4) return false;
    if (ST.postcode) return ST.postcode.set(pc, loc);   // fires st:postcode -> re-quote + announce below
    lsSet(PC_KEY, pc);
    const L = LOC();
    if (L) L.set(loc && String(loc.postcode) === pc ? loc : null);
    ST.__place = { pc, loc: readLoc() };                 // the pre-paint cache (snippets/locality-boot.liquid)
    document.dispatchEvent(new CustomEvent('st:postcode', { detail: { postcode: pc, locality: readLoc() } }));
    return true;
  };
  // "Lane Cove 2064" in copy, "Lane Cove NSW 2064" back in the field, "2064" with no suburb
  const place = (pc, loc) => (loc && loc.suburb ? loc.suburb + ' ' + pc : pc);
  const fieldText = (pc, loc) => (loc && LOC() ? LOC().label(loc, true) : place(pc, loc));

  const state = () => ({ store: readStore(), postcode: readPc() || null, locality: readLoc() });
  const announce = () => document.dispatchEvent(new CustomEvent('st:fulfilment', { detail: state() }));

  const form = root.querySelector('[data-delivery-form]');
  const input = form && form.querySelector('[data-locality-input]');
  const list = form && form.querySelector('[data-locality-list]');
  const result = root.querySelector('[data-delivery-result]');
  const freight = result ? (result.getAttribute('data-freight') || '') : '';
  const contactUrl = result ? (result.getAttribute('data-contact-url') || '') : '';

  /* ---------- quote ---------- */
  let variantFromEvent = '';
  const variantId = () => {
    if (variantFromEvent) return variantFromEvent;
    const el = document.querySelector('[data-product-form] [name="id"]');
    return el ? String(el.value || '').replace(/\D/g, '') : '';
  };
  const qty = () => {
    const el = document.querySelector('[data-product-form] [name="quantity"]');
    const n = el ? parseInt(el.value, 10) : NaN;
    return n > 0 ? n : 1;
  };
  const money = (cents) => '$' + (Math.round(Number(cents)) / 100).toFixed(2);
  const dayWord = (n) => n + (n === 1 ? ' business day' : ' business days');
  // "" unless the carrier gave a window: a null eta is not a slow eta
  const etaText = (eta) => {
    if (!eta || typeof eta !== 'object') return '';
    const a = Number(eta.min_days), b = Number(eta.max_days);
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < a) return '';
    return 'Arrives in ' + (a === b ? dayWord(a) : a + '–' + dayWord(b));
  };
  const spinner = () => {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'hbuy__spin'); svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'M12 3a9 9 0 1 1-6.4 2.6');
    svg.appendChild(path);
    return svg;
  };
  const paint = (main, sub, cls) => {
    if (!result) return;
    result.textContent = '';
    const m = document.createElement('span'); m.className = 'hbuy__est-main'; m.textContent = main; result.appendChild(m);
    if (sub) { const s = document.createElement('span'); s.className = 'hbuy__est-sub'; s.textContent = sub; result.appendChild(s); }
    result.classList.remove('is-set', 'is-error', 'is-loading', 'is-fallback');
    if (cls) result.classList.add(cls);
    return m;
  };
  const showFallback = (pc, loc) => paint('Delivery quoted at checkout for ' + place(pc, loc) + '.', freight, 'is-fallback');
  /* No shipping weight/dimensions recorded against the variant, so no carrier
   * can be quoted at all. Say so plainly and route to support — never a price,
   * and never a "try again" that cannot work.
   *
   * This is the TALLEST state the line can reach without the shopper touching
   * anything, so it is the one snippets/purchase-block.liquid renders as the
   * hidden .hbuy__est-reserve copy that holds the space open. Change the
   * wording here and change it there in the same commit, or the reserve stops
   * matching what it is reserving for. */
  const showNoDimensions = (pc, loc) => {
    const m = paint("We can't calculate delivery for this item online.", freight, 'is-fallback');
    if (!m || !contactUrl) return;
    const a = document.createElement('a');
    a.className = 'hbuy__est-contact';
    a.href = contactUrl;
    a.textContent = 'Contact us for a delivery quote';
    result.appendChild(a);
  };
  const rateLine = (r) => {
    const price = Number(r.price_cents) === 0 ? 'free' : 'from ' + money(r.price_cents);
    const via = r.carrier ? ' via ' + String(r.carrier) : '';
    const eta = etaText(r.eta);
    return price + via + (eta ? ' — ' + eta.replace(/^Arrives in /, 'arrives in ') : '');
  };
  const showQuote = (q, pc, loc) => {
    /* rates[]: cheapest, plus fastest when the backend found a genuinely
     * different option. One carrier connected = one unlabelled row, so the
     * same option is never shown twice under two headings. */
    const rates = Array.isArray(q.rates) ? q.rates.filter((r) => r && Number.isFinite(Number(r.price_cents))) : [];
    if (rates.length > 1) {
      const m = paint('Delivery to ' + place(pc, loc), '', 'is-set');
      rates.forEach((r) => {
        const label = r.label === 'cheapest' ? 'Cheapest: ' : r.label === 'fastest' ? 'Fastest: ' : '';
        const li = document.createElement('span');
        li.className = 'hbuy__est-sub';
        li.textContent = label + rateLine(r);
        result.appendChild(li);
      });
      return m;
    }
    const one = rates.length === 1 ? rates[0] : q;
    const price = Number(one.price_cents) === 0 ? 'free' : 'from ' + money(one.price_cents);
    const via = one.carrier ? ' via ' + String(one.carrier) : '';
    paint('Delivery to ' + place(pc, loc) + ' — ' + price + via, etaText(one.eta), 'is-set');
  };
  const validQuote = (j) => !!(j && j.ok === true && Number.isFinite(Number(j.price_cents)) && Number(j.price_cents) >= 0);
  const isNoDimensions = (j) => !!(j && j.ok === false && j.reason === 'no_dimensions');
  /* Cache sentinel: distinguishes "quoted: impossible" from "quote failed". */
  const NO_DIMS = { noDims: true };

  const show = (q, pc, loc) => {
    if (q === NO_DIMS) showNoDimensions(pc, loc);
    else if (q) showQuote(q, pc, loc);
    else showFallback(pc, loc);
  };

  const quotes = new Map();
  let ctl = null, seq = 0, spinTimer = null;
  const settle = () => { clearTimeout(spinTimer); if (result) result.removeAttribute('aria-busy'); };
  const quote = (pc, loc) => {
    const vid = variantId();
    if (!vid || !PC.test(pc)) { settle(); showFallback(pc, loc); return; }
    const key = vid + '|' + pc + '|' + qty();
    if (quotes.has(key)) { settle(); show(quotes.get(key), pc, loc); return; }
    if (ctl) ctl.abort();
    ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const my = ++seq;
    clearTimeout(spinTimer);
    result.setAttribute('aria-busy', 'true');
    spinTimer = setTimeout(() => {
      if (my !== seq) return;
      const m = paint('Checking delivery to ' + place(pc, loc) + '…', '', 'is-loading');
      if (m) m.prepend(spinner());
    }, SPINNER_AFTER);
    const done = (q) => {
      if (my !== seq) return;
      settle();
      quotes.set(key, q);
      show(q, pc, loc);
    };
    fetch(EST_URL + '?variant=' + encodeURIComponent(vid) + '&postcode=' + pc + '&qty=' + qty(),
      { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: ctl && ctl.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((j) => done(validQuote(j) ? j : isNoDimensions(j) ? NO_DIMS : null))
      .catch((err) => { if (err && err.name === 'AbortError') return; done(null); });
  };

  const paintEstimate = () => {
    if (!result) return;
    const pc = readPc();
    const loc = pc ? readLoc() : null;
    if (input && document.activeElement !== input) {
      const t = pc ? fieldText(pc, loc) : '';
      if (input.value !== t) input.value = t;
    }
    if (!pc) {
      seq++; settle();
      paint('Enter your suburb or postcode for a delivery estimate.', '');
      if (optsBox) optsBox.hidden = true;
      showSentence(true);
      return;
    }
    quote(pc, loc);
    fetchOptions(pc, loc);
  };

  /* ---------- delivery options (the list, not the sentence) ----------
   *
   * Luke, 2026-09-14, on the Alveri Egg bath PDP: "It should show as one of
   * multiple shipping options as the picked default option in the delivery for
   * this item."
   *
   * NOTHING HERE COMPUTES A PRICE. Every figure comes from
   * /apps/sydneytaps/delivery-estimate?options=1, which builds a one-line cart
   * and runs the SAME splitCart + decideRates the CarrierService runs. A widget
   * that did its own arithmetic would eventually disagree with the till, which
   * is the defect this whole lane exists to prevent.
   *
   * The radios are VISUAL ONLY — Shopify decides shipping at checkout and a
   * product page cannot pre-select a rate. The hint says so out loud rather
   * than letting a shopper believe they have chosen something.
   *
   * On any failure the list stays hidden and the sentence above it — the
   * existing fallback, with its measured CLS reserve — is what the shopper
   * sees. Silence is the safe direction here: a missing list costs information,
   * a wrong list costs trust.
   */
  const optsBox = document.querySelector('[data-delivery-options]');
  const estBox = document.querySelector('.hbuy__est-box');
  /* The sentence and the list answer the SAME question, so exactly one of them
   * is on screen at a time. Shown together they contradict each other in the
   * worst possible way: "Delivery quoted at checkout for 2064. Bulky items ship
   * by freight and are quoted at checkout." sitting directly above a list that
   * says $100.00. Caught by looking at the screenshot, not by any assertion.
   *
   * The whole .hbuy__est-box goes, not just the live paragraph: the box also
   * holds the hidden tallest-state copy that reserves its height, so hiding the
   * paragraph alone would leave a block of dead space. The options list has its
   * own skeleton, so nothing jumps while it loads. */
  const showSentence = (on) => { if (estBox) estBox.hidden = !on; };
  // TWO counters, not one. `optGroup` names the radio group so a re-render
  // cannot inherit the previous group's checked state; `optReq` discards a slow
  // response that a newer request has overtaken. Sharing one variable made the
  // staleness check meaningless — a postcode typed quickly could have painted
  // the first postcode's prices.
  let optGroup = 0;
  let optReq = 0;
  /* ETAs are a staff switch, default OFF: no carrier has promised a window yet,
   * and "3–5 business days" invented by a theme is the exact claim Luke struck
   * off this page in September ("if no delivery time window estimate can be
   * given, it's better not to mention it at all"). False means NO time text —
   * not a placeholder, not an em dash. The endpoint also nulls every eta when
   * the switch is off, so this is the second of two locks on one door. */
  let lastShowEtas = false;

  /** {minDays,maxDays} -> "2–4 business days". Strings pass through: the
   *  pre-service-rows endpoint sent this already formatted. */
  const optEtaText = (eta, show) => {
    if (!show || !eta) return '';
    if (typeof eta === 'string') return eta;
    const a = Number(eta.minDays), b = Number(eta.maxDays);
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < a) return '';
    return a === b ? dayWord(a) : a + '–' + dayWord(b);
  };

  const optIcon = (bulky) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('class', 'hbuy__opt-ic');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = bulky
      ? '<rect x="3.5" y="9" width="17" height="9" rx="1"/><path d="M3.5 13h17M8 18v2.5M16 18v2.5"/>'
      : '<path d="M2.5 6h11v10h-11zM13.5 9.5h4l3 3V16h-7z"/><circle cx="6.5" cy="17.5" r="1.8"/><circle cx="16.5" cy="17.5" r="1.8"/>';
    return svg;
  };

  const skeleton = () => {
    optsBox.textContent = '';
    for (let i = 0; i < 2; i += 1) {
      const row = document.createElement('span');
      row.className = 'hbuy__opt hbuy__opt--skel';
      optsBox.appendChild(row);
    }
    optsBox.hidden = false;
  };

  const renderOptions = (j) => {
    if (!optsBox) return;
    optsBox.textContent = '';
    if (!j || j.ok !== true) { optsBox.hidden = true; showSentence(true); return; }

    if (j.blocked && j.blocked.message) {
      const p = document.createElement('p');
      p.className = 'hbuy__opt-blocked';
      p.textContent = j.blocked.message + ' ';
      const a = document.createElement('a');
      a.href = (optsBox.dataset.guidelines || '/pages/delivery') + '#contact';
      a.textContent = 'Contact us';
      p.appendChild(a);
      optsBox.appendChild(p);
      optsBox.hidden = false;
      showSentence(false);
      return;
    }

    /* A BULKY ITEM HAS EXACTLY TWO ROWS, and this is the belt to the server's
     * braces. `options=1` already returns only the pallet row and Click &
     * Collect for a bulky item; filtering again here means a future change on
     * either side cannot put a $15 Express line under a $100 pallet charge
     * without the gate (scripts/pdp-delivery-gate.mjs) failing first. No
     * arithmetic is done — this chooses which rows the server sent get drawn,
     * the same way Delivery.jsx drops pickup from the cart. */
    lastBulky = !!j.bulky;
    lastShowEtas = j.showEtas === true;
    const all = (Array.isArray(j.options) ? j.options : []).filter((o) => {
      // EXPRESS IS NEVER FREE (Luke, 2026-09-15). Free over $1,000 is a Standard
      // Shipping offer; Express is a different service with its own price. An
      // Express row at $0 is a contract violation upstream, and repeating it to
      // a shopper as "Free" is a promise the checkout will refuse. Dropping the
      // row loses information; drawing it loses trust.
      if (o && o.id === 'express' && o.price_cents === 0) {
        console.warn('delivery: refusing to render a free Express row', o);
        return false;
      }
      return !!o;
    });
    const list = lastBulky ? all.filter((o) => o.fixed || o.id === 'pickup') : all;
    if (!list.length) { optsBox.hidden = true; showSentence(true); return; }

    const name = 'hbuy-opt-' + (++optGroup);
    // When the fixed charge is the ONLY thing on offer (a delivery-only
    // pallet item with no Click & Collect), it is not "charged whatever else
    // you pick" — it is the delivery. It renders selected, not muted. Luke,
    // 2026-09-16: "I'm trying to understand why bulky goods delivery is shown
    // as greyed out."
    const choices = list.filter((o) => !o.fixed && !o.informational);
    const soleFixed = choices.length === 0;
    list.forEach((o, i) => {
      const label = document.createElement('label');
      label.className = 'hbuy__opt' + (o.informational ? ' hbuy__opt--info' : '');
      if (o.fixed && !soleFixed) {
        // Part A. Charged whatever else the shopper picks, so it gets no radio
        // at all: offering a choice a shopper cannot decline is a lie the
        // layout tells. Luke, 2026-09-15: bulky goods "will be separate from
        // the rate calculation for the rest of the cart".
        label.className += ' hbuy__opt--fixed';
        const mark = document.createElement('span');
        mark.className = 'hbuy__opt-radio hbuy__opt-radio--fixed';
        mark.setAttribute('aria-hidden', 'true');
        label.appendChild(mark);
      } else {
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.className = 'hbuy__opt-radio';
        // A null price means "carrier-calculated, not known yet": it renders as
        // "quoted at checkout" and must never arrive pre-selected, because the
        // row a shopper sees ticked is the one they believe they are paying.
        input.checked = (o.default === true || (o.fixed && soleFixed)) && o.price_cents != null;
        // Informational rows are not a choice: they say the offer exists and how
        // far off it is. Making them selectable would imply the shopper could
        // pick "free" on a $258 order. This is the id 'free' row — the greyed
        // "add $X more to qualify" line, which is NOT the standard service and
        // never means the cart has qualified.
        if (o.informational) input.disabled = true;
        label.appendChild(input);
      }
      label.appendChild(optIcon(j.bulky && i === 0));

      const text = document.createElement('span');
      text.className = 'hbuy__opt-text';
      const main = document.createElement('span');
      main.className = 'hbuy__opt-main';
      main.textContent = o.label;
      text.appendChild(main);
      const when = optEtaText(o.eta, lastShowEtas);
      if (o.sublabel || when) {
        const sub = document.createElement('span');
        sub.className = 'hbuy__opt-sub';
        sub.textContent = [o.sublabel, when].filter(Boolean).join(' · ');
        text.appendChild(sub);
      }
      label.appendChild(text);

      const price = document.createElement('span');
      price.className = 'hbuy__opt-price';
      price.textContent = o.price_cents == null ? 'quoted at checkout'
        : o.price_cents === 0 ? 'Free' : money(o.price_cents);
      label.appendChild(price);
      optsBox.appendChild(label);
    });

    const hint = document.createElement('p');
    hint.className = 'hbuy__opt-hint';
    hint.textContent = 'Chosen at checkout';
    optsBox.appendChild(hint);
    optsBox.hidden = false;
    showSentence(false);
    paintCartNote();
  };

  /* ONE LINE: this product, at the quantity in the box above.
   *
   * `options=1` is the item-scoped mode of the same endpoint, running the same
   * splitCart + decideRates the CarrierService runs at checkout. It is also
   * where the bulky rule lives: a bulky item comes back as the pallet row plus
   * Click & Collect and nothing else. Routing this widget through `cart=1`
   * (2026-09-15, earlier today) bypassed that rule, which is how a toilet PDP
   * came to offer "Free shipping — Free" beside a $400 pallet charge once the
   * cart held $1,068 of mixers — the cart's Part B, rendered under a heading
   * about a bath. Luke: "free shipping is showing on the delivery estimator for
   * a bulky item, when it should never be an option presented to them in the
   * delivery estimator on the product page for a bulky item, it should always
   * be $100 flat rate/unit."
   */
  const fetchOptions = (pc, loc) => {
    if (!optsBox) return;
    const vid = variantId();
    const pid = optsBox.dataset.product;
    if (!vid || !pid || !PC.test(pc)) { optsBox.hidden = true; showSentence(true); return; }
    const my = ++optReq;
    skeleton();
    // Delivery-only products (tiles: they go straight from the warehouse to
    // the door, never through our dispatch) are never offered Click & Collect.
    // Luke, 2026-09-16: "Click and Collect isn't even an option, it's only
    // Bulky goods delivery." The block already knows (data-fulfilment) and
    // tells the app, which then omits the pickup row and selects delivery.
    const hbuy = optsBox.closest('[data-purchase]');
    const deliveryOnly = !!(hbuy && hbuy.dataset.fulfilment === 'delivery_only');
    const url = EST_URL + '?options=1&variant=' + encodeURIComponent(vid)
      + '&product=' + encodeURIComponent(pid)
      + (deliveryOnly ? '&pickup=0' : '')
      + '&price=' + encodeURIComponent(optsBox.dataset.price || '0')
      + '&qty=' + qty() + '&postcode=' + pc
      + (loc && loc.suburb ? '&suburb=' + encodeURIComponent(loc.suburb) : '');
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((j) => { if (my === optReq) renderOptions(j); })
      .catch(() => { if (my === optReq) { optsBox.hidden = true; showSentence(true); } });
  };

  /* THE ONE CART-AWARE TOUCH, AND IT IS A NOTE RATHER THAN A PRICE.
   *
   * Luke, 2026-09-15 (before the two screenshots above): "when free shipping
   * tier is reached in the cart this needs to show free shipping as an option".
   * The half of that worth keeping is that a shopper who has ALREADY earned
   * free standard delivery should not be told on a product page that they are
   * $594.70 short of it. The half that caused the confusion is pricing this
   * page against the cart.
   *
   * So the cart changes one SENTENCE here and never a number: a muted line
   * saying the cart already qualifies. No figure in it can disagree with the
   * options above it, because it carries no figure.
   *
   * Where the answer comes from: the cart island has already asked — delivery.js
   * quotes the whole cart for the drawer on every page and publishes the result
   * on `st:delivery`. Reading that costs no request and, more importantly,
   * cannot produce a second opinion: there is one cart quote on the page and
   * both surfaces read it. The test is "the cart's own Part B contains a free
   * standard option that is actually selectable" — an `informational` row is the
   * greyed 'add $X more' state, which is the opposite of qualifying.
   */
  let cartQualifies = false;
  let lastBulky = false;

  const paintCartNote = () => {
    if (!optsBox || optsBox.hidden) return;
    const existing = optsBox.querySelector('[data-cart-qualifies]');
    // Never on a bulky item: the pallet rate is charged per unit whatever else
    // is in the basket, so "your cart qualifies for free delivery" under a $400
    // pallet charge reads as a contradiction of the row above it.
    const want = cartQualifies && !lastBulky;
    if (!want) { if (existing) existing.remove(); return; }
    if (existing) return;
    const p = document.createElement('p');
    p.className = 'hbuy__opt-note';
    p.setAttribute('data-cart-qualifies', '');
    p.textContent = 'Your cart already qualifies for free standard delivery on its non-bulky items';
    const hint = optsBox.querySelector('.hbuy__opt-hint');
    optsBox.insertBefore(p, hint || null);
  };

  /* THE TEST IS "STANDARD IS FREE", NOT "SOMETHING IS FREE".
   *
   * Free over $1,000 is an offer on Standard Shipping alone (Luke, 2026-09-15);
   * Express is a different service and is never free. The first version of this
   * asked whether any non-pickup row was $0, which was only correct while
   * Standard was the one row that could be — and would have started announcing
   * "free standard delivery" off the back of some other $0 service the day one
   * existed.
   *
   * TWO ROWS LOOK ALIKE AND MEAN OPPOSITE THINGS. `id: 'standard'` at $0 is the
   * offer EARNED; `id: 'free'` at $0 is the greyed "add $X more to qualify" row,
   * which exists precisely when the cart has NOT earned it. Testing `standard`
   * and excluding `informational` rejects the second twice over — and Click &
   * Collect, also $0, is neither.
   *
   * Reads whichever shape the cart quote is in; see quoteRows in Delivery.jsx
   * for why both are accepted during the changeover.
   */
  document.addEventListener('st:delivery', (e) => {
    const st = e && e.detail;
    const q = (st && st.quote) || null;
    const rows = q ? (Array.isArray(q.options) ? q.options : (q.part_b || [])) : [];
    const next = rows.some((o) => o && o.id === 'standard'
                                 && o.price_cents === 0 && !o.informational);
    if (next === cartQualifies) return;
    cartQualifies = next;
    paintCartNote();
  });

  /* ---------- API ---------- */
  ST.fulfilment = {
    get: state,
    setStore(id) {
      const hit = opts.find((o) => o.value === id);
      if (!hit) return false;
      lsSet(STORE_KEY, id); paintStore(); announce();
      return true;
    },
    setPostcode: writePc,
  };

  /* ---------- wiring ---------- */
  if (picker) {
    const toggle = picker.querySelector('[data-store-toggle]');
    const storeList = picker.querySelector('[data-store-list]');
    const open = () => { storeList.hidden = false; toggle.setAttribute('aria-expanded', 'true'); };
    const close = () => { storeList.hidden = true; toggle.setAttribute('aria-expanded', 'false'); };
    toggle.addEventListener('click', () => (storeList.hidden ? open() : close()));
    opts.forEach((o) => o.addEventListener('change', () => {
      if (!o.checked) return;
      lsSet(STORE_KEY, o.value); paintStore(); announce();
      if (opts.length > 1) setTimeout(close, 120);
    }));
    document.addEventListener('click', (e) => { if (!storeList.hidden && !picker.contains(e.target)) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !storeList.hidden) { close(); toggle.focus(); } });
    paintStore();
  }
  if (form && input) {
    // the typeahead (locality.js) when the header loaded it; a plain postcode field otherwise
    const field = list && LOC() ? LOC().attach(input, { list, onSelect: (loc) => { writePc(loc.postcode, loc); } }) : null;
    const invalid = () => {
      input.setCustomValidity(field ? 'Choose a suburb from the list or enter a 4-digit postcode' : 'Enter a 4-digit Australian postcode');
      input.reportValidity();
      input.setCustomValidity('');
    };
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!field) { if (!writePc(input.value)) invalid(); return; }
      field.resolve().then((r) => {
        if (!r) { invalid(); return; }
        if (r.locality) return;                    // onSelect wrote it
        if (!writePc(r.postcode)) invalid();
      });
    });
  }
  /* A new postcode can move the collection store (readStore step 2), so repaint
   * the picker before the estimate — the Click & Collect copy names the store
   * and must not lag a beat behind the delivery card. */
  document.addEventListener('st:postcode', () => { if (picker) paintStore(); paintEstimate(); announce(); });
  document.addEventListener('variant:change', (e) => {
    if (e.detail && e.detail.id) variantFromEvent = String(e.detail.id);
    if (readPc()) paintEstimate();
  });
  /* Quantity is now the only thing on the page that moves these numbers, so it
   * gets both events, not just `change`:
   *   change — the stepper's -/+ and arrow keys re-dispatch it (product.js
   *     normalises in the capture phase first, so qty() reads the clamped
   *     value), and a typed field fires it on blur or Enter.
   *   input  — a typed digit, debounced, so "2" shows two pallets while the
   *     caret is still in the box. Skipped while the field is empty or
   *     mid-edit ("0", ""), which `change` will normalise to 1 anyway.
   * Both land on paintEstimate, whose per-request counters (optReq) already
   * discard an overtaken response. */
  const qtyEl = document.querySelector('[data-product-form] [name="quantity"]');
  if (qtyEl) {
    qtyEl.addEventListener('change', () => { if (readPc()) paintEstimate(); });
    let qtyTimer = null;
    qtyEl.addEventListener('input', () => {
      if (!/^\d+$/.test(qtyEl.value) || parseInt(qtyEl.value, 10) < 1) return;
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(() => { if (readPc()) paintEstimate(); }, 300);
    });
  }
  // first paint once every deferred script has run: locality.js and the
  // header (SydneyTaps.postcode) both load before this one today, but the
  // quote must not depend on script order
  const firstPaint = () => { if (picker) paintStore(); paintEstimate(); };
  if (document.readyState === 'complete') firstPaint();
  else document.addEventListener('DOMContentLoaded', firstPaint);
})();
